import { useCallback, useMemo, useReducer, useRef } from "react";
import axios from "axios";
import { Box, Flex, Heading, HStack, Image, useColorMode, useDisclosure, VStack } from "@chakra-ui/react";
import { useAlertToast } from "./AlertToast";
import { isMobile } from "react-device-detect";
import { AppContext } from "./AppContext";
import useLocalStorage from "use-local-storage";
import { SettingsModal } from "./SettingsModal/SettingsModal";
import ReactGA from "react-ga4";
import {
  checkIsQueryComplete,
  parseString,
  reconstructQueryString,
  getMaxChapter,
  getKeywords,
  getActualLastVerse,
  toEnglishBookName,
} from "./ParseString";
import { generateOptions } from "./GenerateSelectorOptions";
import EasyBibleLogo from "./img/easy-bible-logo-nobg.png";
import { VerseDisplayArea } from "./VerseDisplayArea";
import Footer from "./Footer";
import { InputComponents } from "./InputComponents";
import { LanguageType, VersionType } from "./constants";
import { Language } from "./Language";
import qs from "qs";
import { initialSearchState, searchReducer } from "./appReducer";

// Google Analytics Performance
ReactGA.initialize("G-6V70TLXZRR");
ReactGA.send("pageview");

const localDev = false;
export const baseUrl = localDev ? "http://127.0.0.1:5000" : "https://easy-bible.herokuapp.com";

export const pageWidth = isMobile ? "90vw" : "80vw";
export const pageMargin = isMobile ? "5vw" : "10vw";

function App() {
  const [searchState, dispatch] = useReducer(searchReducer, initialSearchState);
  const {
    query,
    verse,
    isLoading,
    isSuggestionsOpen,
    isHistoryOpen,
    options,
    isQueryComplete,
    isAtMaxChapter,
    searchHistory,
    fetchedAsComplete,
  } = searchState;

  const [textAreaFontSize, setTextAreaFontSize] = useLocalStorage("textAreaFontSize", 16);
  const [textAreaLineHeight, setTextAreaLineHeight] = useLocalStorage("textAreaLineHeight", 1.5);
  const [copyWithVerse, setCopyWithVerse] = useLocalStorage("copyWithVerse", false);
  const [viewMode, setViewMode] = useLocalStorage<"combined" | "verses">("viewMode", "combined");
  const [language, setLanguage] = useLocalStorage<LanguageType>("language", "EN");
  const [versions, setVersions] = useLocalStorage("versions", [VersionType.ESV]);

  const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen: isSettingsOpen, onOpen: _openSettings, onClose: closeSettings } = useDisclosure();
  const settingsPageRef = useRef(1);
  const openSettings = useCallback(
    (page = 1) => {
      settingsPageRef.current = page;
      _openSettings();
    },
    [_openSettings],
  );
  const ref = useRef<HTMLInputElement | null>(null);
  const hasShownChineseVersionToast = useRef(false);
  const showAlertToast = useAlertToast();

  const isEnglish = language === "EN";
  const viewModeVerse = viewMode === "combined" && fetchedAsComplete ? verse.replaceAll("\n", " ") : verse;
  const disableChapterRelatedButton = !isQueryComplete || !verse || isLoading;
  const instructionToastId = "show-tips-toast";
  const chineseVersionToastId = "suggest-chinese-version-toast";

  const selectorOptions = useMemo(() => generateOptions(versions), [versions]);
  const errorMsgs = useMemo(() => ["No inputs!", "Verse not found!", "Typo existed!", "NOT FOUND"], []);

  const setQuery = (query: string) => dispatch({ type: "SET_QUERY", query });
  const setVerse = (verse: string) => dispatch({ type: "SET_VERSE", verse });
  const setIsSuggestionsOpen = (value: boolean) => dispatch({ type: "SET_SUGGESTIONS_OPEN", value });
  const setIsHistoryOpen = (value: boolean) => dispatch({ type: "SET_HISTORY_OPEN", value });

  const handleChange = (e: any) => {
    const query = e.target.value;
    const isQueryComplete = checkIsQueryComplete(query);
    const newOptions = selectorOptions.filter(
      (option) => option.label.toLowerCase().includes(query.toLowerCase()) && option.label !== query,
    );
    dispatch({
      type: "INPUT_CHANGE",
      query,
      options: newOptions,
      isSuggestionsOpen: newOptions.length > 0,
      isHistoryOpen: query.length === 0 && searchHistory.length > 0,
      isQueryComplete,
    });
  };

  const handleSubmit = useCallback(
    async (versions: VersionType[], query: string) => {
      if (isLoading) return;

      const isQueryComplete = checkIsQueryComplete(query);
      let newQuery = query;
      let verseData;

      if (!isQueryComplete) {
        // Keyword search
        const keywords = getKeywords(query);

        if (keywords.length === 0) {
          dispatch({ type: "SET_VERSE", verse: "No keywords provided!" });
          return;
        }

        if (versions.includes(VersionType.ESV)) {
          dispatch({ type: "SET_VERSE", verse: "Keyword search is not supported for ESV." });
          showAlertToast({
            status: "warning",
            message: "Switch to a Chinese version to use keyword search.",
            buttonLabel: "Open Settings",
            onButtonClick: () => openSettings(1),
            duration: 5000,
          });
          return;
        }

        dispatch({ type: "FETCH_START", isQueryComplete: false, isAtMaxChapter: false });
        const { data } = await axios.get(`${baseUrl}/keywords-search`, {
          params: { versions, keywords },
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
        });
        verseData = data;
      } else {
        // Regular verse search
        let { book, chapter, verseStart, verseEnd } = parseString(query.replace("：", ":"));
        if (!(book && chapter)) {
          dispatch({ type: "SET_VERSE", verse: "No book or chapter provided!" });
          return;
        }

        const maxChapterNum = getMaxChapter(book);
        if (maxChapterNum && chapter > maxChapterNum) {
          chapter = maxChapterNum;
        }
        const atMaxChapter = chapter >= maxChapterNum;

        // swap verseStart and verseEnd if verseEnd is smaller than verseStart
        if (verseEnd && verseStart && verseEnd < verseStart) {
          const temp = verseEnd;
          verseEnd = verseStart;
          verseStart = temp;
        }

        newQuery = reconstructQueryString(query.replace("：", ":"), book, chapter, verseStart, verseEnd, versions);
        dispatch({ type: "FETCH_START", isQueryComplete: true, isAtMaxChapter: atMaxChapter });
        const bookForApi = versions.includes(VersionType.ESV) ? toEnglishBookName(book) : book;
        const { data } = await axios.get(`${baseUrl}/verse`, {
          params: { versions, book: bookForApi, chapter, verseStart, verseEnd },
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
        });
        verseData = data;

        // Update verseEnd in query if backend returned fewer verses than requested
        if (verseEnd) {
          const isESV = versions.includes(VersionType.ESV);
          const actualLastVerse = getActualLastVerse(verseData, isESV);
          if (actualLastVerse !== undefined && actualLastVerse < verseEnd) {
            verseEnd = actualLastVerse;
            newQuery = reconstructQueryString(query.replace("：", ":"), book, chapter, verseStart, verseEnd, versions);
          }
        }
      }

      dispatch({
        type: "FETCH_SUCCESS",
        query: newQuery,
        verse: verseData,
        searchHistory: [...searchHistory, newQuery],
        fetchedAsComplete: isQueryComplete,
      });

      if (ref.current && isMobile) {
        ref.current.blur(); // lose focus on input on Mobile, and disable keyboard
      }

      // Suggest Chinese version if query is Chinese but only ESV is selected (once per session)
      const queryHasChinese = /[\u4e00-\u9fa5]/.test(query);
      if (
        queryHasChinese &&
        versions.length === 1 &&
        versions.includes(VersionType.ESV) &&
        !hasShownChineseVersionToast.current
      ) {
        hasShownChineseVersionToast.current = true;
        showAlertToast({
          id: chineseVersionToastId,
          status: "info",
          message: "Your search is in Chinese. Consider switching to a Chinese version.",
          buttonLabel: "Open Settings",
          onButtonClick: () => openSettings(1),
          duration: 8000,
        });
      }

      // Display instruction Toast
      if (errorMsgs.includes(verseData))
        showAlertToast({
          id: instructionToastId,
          status: "info",
          message: "Check the tips section in Settings!",
          buttonLabel: "Open Tips",
          onButtonClick: () => openSettings(2),
          duration: 4000,
        });
    },
    [errorMsgs, showAlertToast, isLoading, searchHistory, openSettings],
  );

  return (
    <AppContext.Provider
      value={{
        query,
        setQuery,
        handleChange,
        handleSubmit,
        openSettings,
        versions,
        setVersions,
        colorMode,
        toggleColorMode,
        textAreaFontSize,
        setTextAreaFontSize,
        textAreaLineHeight,
        setTextAreaLineHeight,
        copyWithVerse,
        setCopyWithVerse,
        viewMode,
        setViewMode,
        isEnglish,
        language,
        setLanguage,
      }}
    >
      <div className="App">
        <HStack
          m="1rem"
          mb={"20px"}
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          spacing={"auto"}
        >
          {isMobile ? <></> : <Box />} {/* Empty box to align with Settings button */}
          <Flex alignItems="center" cursor={"pointer"} onClick={() => dispatch({ type: "RESET" })}>
            {/* Align items vertically */}
            <Image src={EasyBibleLogo} alt="Easy Bible App Logo" boxSize="40px" mr={3} />{" "}
            {/* Adjust size and margin as needed */}
            <Heading as="h2" size={{ base: "md", sm: "lg" }}>
              Bible Verse Search
            </Heading>
          </Flex>
          <HStack>
            <Language />
            <SettingsModal
              isOpen={isSettingsOpen}
              onOpen={() => openSettings()}
              onClose={closeSettings}
              initialPage={settingsPageRef.current}
            />
          </HStack>
        </HStack>
        <VStack w={pageWidth} ml={pageMargin} mr={pageMargin} mt={isMobile ? "0.5vh" : "5vh"} pb="40px">
          <InputComponents
            ref={ref}
            errorMsgs={errorMsgs}
            verse={verse}
            isSuggestionsOpen={isSuggestionsOpen}
            setIsSuggestionsOpen={setIsSuggestionsOpen}
            isLoading={isLoading}
            options={options}
            searchHistory={searchHistory}
            isHistoryOpen={isHistoryOpen}
            setIsHistoryOpen={setIsHistoryOpen}
            setVerse={setVerse}
          />
          <div style={{ marginTop: isMobile ? "10px" : "20px" }}></div>
          <VerseDisplayArea
            verse={viewModeVerse}
            setVerse={setVerse}
            errorMsgs={errorMsgs}
            isQueryComplete={isQueryComplete}
          />
          <Footer
            disableChapterRelatedButton={disableChapterRelatedButton}
            viewModeVerse={viewModeVerse}
            isAtMaxChapter={isAtMaxChapter}
          />
        </VStack>
      </div>
    </AppContext.Provider>
  );
}

export default App;
